import { useEffect, useState } from "react";
import api, { apiError } from "@/lib/apiClient";
import { toast } from "sonner";
import PageHeader from "@/components/PageHeader";
import { useAuth } from "@/context/AuthContext";
import { CardsSkeleton, ErrorState } from "@/components/States";
import { InfoBlock, OfferBlock, GoalsBlock, ProfilesBlock, OperativeBlock, Section, inputCls } from "@/pages/app/brain/BrainSections";
import { deriveIdealClient } from "@/lib/onboardingConfig";
import VetoedAccounts from "@/components/VetoedAccounts";
import { Save, Plus, Trash2, FileText, Link2, RefreshCw, CheckCircle, Pencil, Upload, AlertTriangle, Loader2, Sparkles } from "lucide-react";

export default function Brain() {
  const { user } = useAuth();
  const [form, setForm] = useState(null);
  const [error, setError] = useState(false);
  const [saving, setSaving] = useState(false);
  const [errors, setErrors] = useState({});
  const [newLink, setNewLink] = useState("");
  const [pendingFile, setPendingFile] = useState(null);
  const [linkOpen, setLinkOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [srcBusy, setSrcBusy] = useState(null);
  const [genBusy, setGenBusy] = useState(false);
  const [confirmDel, setConfirmDel] = useState(null);
  const [summaryEditing, setSummaryEditing] = useState(false);

  // v13: quien depende de una cuenta madre ve el negocio del equipo, pero no lo cambia.
  const readonly = user?.team_role === "member" || !!form?.team_readonly;

  const load = () => {
    setError(false);
    api.get("/business-brain").then((r) => {
      setForm({
        business_name: "", offer: "", problem: "", market: "", market_scope: "", market_countries: [],
        products_prices: "", goals: "", resources: "",
        experience_level: "principiante", message_preference: "mensajes", self_intro: "",
        business_type: "", current_acquisition: "", time_availability: "",
        networks: [], networks_from_scratch: [], goals_list: [], priority_offers: "", questionnaire: {},
        avatars: "", avatars_list: [],
        knowledge_sources: [], summary_text: "", summary_approved: false,
        ...r.data,
        // Si el onboarding ya recogió a quién se dirige, se precarga para no pedirlo dos veces.
        ideal_client: (r.data && r.data.ideal_client) || deriveIdealClient(r.data || {}),
      });
    }).catch(() => setError(true));
  };

  useEffect(load, []);

  const set = (k, v) => { setForm((f) => ({ ...f, [k]: v })); if (errors[k]) setErrors((e) => ({ ...e, [k]: "" })); };
  const setQ = (k, v) => setForm((f) => ({ ...f, questionnaire: { ...(f.questionnaire || {}), [k]: v } }));
  const toggleArr = (key, k) => setForm((f) => { const arr = f[key] || []; return { ...f, [key]: arr.includes(k) ? arr.filter((x) => x !== k) : [...arr, k] }; });
  const toggleGoal = (k) => toggleArr("goals_list", k);
  const patch = (p) => { setForm((f) => ({ ...f, ...p })); if (errors.market) setErrors((e) => ({ ...e, market: "" })); };

  const applyBrain = (data) => setForm((f) => ({ ...f, ...data }));

  const onPickFile = (e) => {
    const file = e.target.files && e.target.files[0];
    e.target.value = "";
    if (!file) return;
    const ext = (file.name.split(".").pop() || "").toLowerCase();
    const ok = ["pdf", "docx", "txt", "csv", "jpg", "jpeg", "png"];
    if (!ok.includes(ext)) { toast.error("Formato no admitido. Usa PDF, DOCX, TXT, CSV, JPG o PNG."); return; }
    if (file.size > 10 * 1024 * 1024) { toast.error("El archivo supera el límite de 10 MB."); return; }
    setPendingFile(file);
  };

  const uploadFile = async () => {
    if (!pendingFile || busy) return;
    setBusy(true);
    const fd = new FormData();
    fd.append("file", pendingFile);
    try {
      const r = await api.post("/knowledge/file", fd, { headers: { "Content-Type": "multipart/form-data" } });
      applyBrain(r.data);
      const name = pendingFile.name;
      setPendingFile(null);
      const s = [...(r.data.knowledge_sources || [])].reverse().find((x) => x.name === name) || [...(r.data.knowledge_sources || [])].reverse()[0];
      if (s?.status === "error") toast.error(`No se pudo procesar el archivo${s.error ? ": " + s.error : "."}`);
      else if (s?.status === "procesada") toast.success("Archivo procesado correctamente.");
      else toast.info("Procesando archivo…");
    } catch (err) { toast.error(apiError(err)); }
    finally { setBusy(false); }
  };

  const addLink = async () => {
    const url = (newLink || "").trim();
    if (!/^https?:\/\/.+/.test(url)) { toast.error("Pega un enlace válido (empieza por http)."); return; }
    if (busy) return;
    setBusy(true);
    try {
      const r = await api.post("/knowledge/link", { url });
      applyBrain(r.data);
      setNewLink("");
      setLinkOpen(false);
      const s = [...(r.data.knowledge_sources || [])].reverse().find((x) => x.name === url);
      if (s?.status === "error") toast.error(`No se pudo procesar el enlace${s.error ? ": " + s.error : "."}`);
      else if (s?.status === "procesada") toast.success("Enlace procesado correctamente.");
      else toast.info("Procesando enlace…");
    } catch (err) { toast.error(apiError(err)); }
    finally { setBusy(false); }
  };

  const reprocess = async (id) => {
    if (srcBusy) return;
    setSrcBusy(id);
    setForm((f) => ({ ...f, knowledge_sources: (f.knowledge_sources || []).map((s) => s.id === id ? { ...s, status: "procesando" } : s) }));
    try {
      const r = await api.post(`/knowledge/${id}/reprocess`);
      applyBrain(r.data);
      const s = (r.data.knowledge_sources || []).find((x) => x.id === id);
      if (s?.status === "error") toast.error(`No se pudo procesar el enlace${s.error ? ": " + s.error : "."}`);
      else if (s?.status === "procesada") toast.success("Fuente procesada correctamente.");
      else toast.info("Procesando…");
    } catch (err) { toast.error(apiError(err)); load(); }
    finally { setSrcBusy(null); }
  };

  const doDelete = async (id) => {
    try {
      const r = await api.delete(`/knowledge/${id}`);
      applyBrain(r.data);
      setConfirmDel(null);
      toast("Fuente eliminada.");
    } catch (err) { toast.error(apiError(err)); }
  };

  const generateSummary = async () => {
    if (genBusy) return;
    setGenBusy(true);
    try {
      const r = await api.post("/knowledge/summary/generate");
      applyBrain(r.data);
      setSummaryEditing(true);
      toast.success("Resumen generado. Revísalo y apruébalo.");
    } catch (err) { toast.error(apiError(err)); }
    finally { setGenBusy(false); }
  };

  const reopenSummary = async () => {
    if (genBusy) return;
    setGenBusy(true);
    try {
      const r = await api.post("/knowledge/summary/reopen");
      applyBrain(r.data);
      setSummaryEditing(true);
    } catch (err) { toast.error(apiError(err)); }
    finally { setGenBusy(false); }
  };

  const approveSummary = async () => {
    if (!(form.summary_text || "").trim()) { toast.error("Genera o escribe el resumen antes de aprobarlo."); return; }
    if (form.summary_stale) { toast.error("Has eliminado una fuente. Regenera el resumen antes de aprobarlo."); return; }
    if (genBusy) return;
    setGenBusy(true);
    try {
      const r = await api.post("/knowledge/summary/approve", { summary_text: form.summary_text });
      applyBrain(r.data);
      setSummaryEditing(false);
      toast.success("Resumen aprobado. Ya puedes recibir recomendaciones.");
    } catch (err) { toast.error(apiError(err)); }
    finally { setGenBusy(false); }
  };

  const save = async (e) => {
    e.preventDefault();
    const errs = {};
    if (!form.offer.trim()) errs.offer = "Cuéntanos al menos qué ofreces.";
    if (!form.ideal_client.trim() && !deriveIdealClient(form)) errs.ideal_client = "Necesitamos saber a quién ayudas.";
    if (["country", "multiple"].includes(form.market_scope) && !(form.market_countries || []).length) errs.market = "Selecciona al menos un país.";
    setErrors(errs);
    if (Object.keys(errs).length || saving) return;
    setSaving(true);
    try {
      await api.put("/business-brain", form);
      toast.success("Guardado. Tu prospección se ajusta a esto.");
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-8">
      <PageHeader
        title="Mi negocio"
        subtitle="Cuéntanos tu negocio. Con esto afinamos a quién contactar y qué decir en cada mensaje."
        testId="brain-header"
      />

      {form === null && !error && <CardsSkeleton cards={3} />}
      {error && <ErrorState description="No hemos podido cargar tu negocio." onRetry={load} testId="brain-error" />}

      {form && (
        <form onSubmit={save} className="max-w-2xl space-y-8" noValidate>
          {readonly ? (
            <div className="rounded-md border border-brand-accent/40 bg-brand-accent/[0.08] p-4 text-sm text-brand-ink" data-testid="brain-team-readonly">
              Este es el negocio de tu cuenta madre. Todo el equipo prospecta para él, así que solo la cuenta madre puede cambiarlo.
            </div>
          ) : (
            <p className="text-xs text-brand-muted">Se guarda en tu cuenta y sigue ahí mañana.</p>
          )}
          <fieldset disabled={readonly} className="min-w-0 space-y-8">

          <InfoBlock form={form} set={set} setQ={setQ} patch={patch} errors={errors} />
          <OfferBlock form={form} set={set} errors={errors} />
          <GoalsBlock form={form} set={set} toggleGoal={toggleGoal} />
          <ProfilesBlock form={form} setForm={setForm} applyBrain={applyBrain} />
          <OperativeBlock form={form} set={set} toggleArr={toggleArr} />

          {/* Conocimiento del negocio */}
          <div className="ca-card p-6" data-testid="knowledge-section">
            <div className="mb-1 flex flex-wrap items-start justify-between gap-3" data-testid="brain-block-f">
              <div className="flex items-start gap-3">
                <span className="grid h-8 w-8 shrink-0 place-items-center rounded-lg border border-brand-accent/40 bg-brand-accent/[0.08] font-mono text-xs font-bold text-brand-accent">F</span>
                <h2 className="ca-h2 flex items-center gap-2"><FileText size={16} className="text-brand-accent" /> Conocimiento, fuentes y resumen</h2>
              </div>
              <span className="rounded-full border border-brand-accent/40 px-2.5 py-1 font-mono text-[10px] uppercase tracking-wider text-brand-accent">Conocimiento del negocio</span>
            </div>
            <p className="mb-4 text-sm text-brand-muted">Añade documentos y enlaces públicos. CaptAcción extrae y resume su contenido real; no accedemos a contenido privado ni protegido.</p>

            {/* Añadir fuentes */}
            <div className="space-y-3">
              {!pendingFile ? (
                <label data-testid="knowledge-add-file" className="inline-flex cursor-pointer items-center gap-2 rounded-md border border-brand-line bg-brand-panel px-3 py-2 text-xs font-semibold text-brand-ink transition-all duration-150 hover:border-brand-accent hover:text-brand-accent">
                  <Upload size={14} /> Adjuntar archivo
                  <input type="file" accept=".pdf,.docx,.txt,.csv,.jpg,.jpeg,.png" className="hidden" onChange={onPickFile} data-testid="knowledge-file-input" />
                </label>
              ) : (
                <div className="flex flex-wrap items-center gap-3 rounded-md border border-brand-accent/40 bg-brand-accent/[0.06] p-3" data-testid="knowledge-file-preview">
                  <FileText size={16} className="text-brand-accent" />
                  <div className="min-w-0 flex-1">
                    <p className="truncate text-sm font-semibold text-brand-ink">{pendingFile.name}</p>
                    <p className="text-xs text-brand-muted">{(pendingFile.size / 1024 / 1024).toFixed(2)} MB · máx. 10 MB</p>
                  </div>
                  <button type="button" onClick={uploadFile} disabled={busy} data-testid="knowledge-file-upload" className="inline-flex items-center gap-2 rounded-md bg-brand-accent px-3 py-2 text-xs font-bold text-brand-canvas transition-all hover:brightness-110 disabled:opacity-60">
                    {busy ? <Loader2 size={14} className="animate-spin" /> : <Upload size={14} />} {busy ? "Procesando…" : "Subir y procesar"}
                  </button>
                  <button type="button" onClick={() => setPendingFile(null)} disabled={busy} className="rounded-md border border-brand-line bg-brand-panel px-3 py-2 text-xs font-semibold text-brand-ink disabled:opacity-60">Cancelar</button>
                </div>
              )}

              {!linkOpen ? (
                <div>
                  <button type="button" onClick={() => setLinkOpen(true)} data-testid="knowledge-add-link" className="inline-flex items-center gap-2 rounded-md border border-brand-line bg-brand-panel px-3 py-2 text-xs font-semibold text-brand-ink transition-all duration-150 hover:border-brand-accent hover:text-brand-accent"><Link2 size={14} /> Añadir enlace</button>
                </div>
              ) : (
                <div className="flex flex-wrap items-center gap-2 rounded-md border border-brand-accent/40 bg-brand-accent/[0.06] p-3" data-testid="knowledge-link-form">
                  <input autoFocus data-testid="knowledge-link-input" className={`${inputCls} flex-1`} value={newLink} onChange={(e) => setNewLink(e.target.value)} placeholder="https://tu-web.com" onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addLink(); } }} />
                  <button type="button" onClick={addLink} disabled={busy} data-testid="knowledge-link-save" className="inline-flex shrink-0 items-center gap-2 rounded-md bg-brand-accent px-3 py-2 text-xs font-bold text-brand-canvas transition-all hover:brightness-110 disabled:opacity-60">
                    {busy ? <Loader2 size={14} className="animate-spin" /> : <Plus size={14} />} {busy ? "Procesando…" : "Guardar"}
                  </button>
                  <button type="button" onClick={() => { setLinkOpen(false); setNewLink(""); }} disabled={busy} className="rounded-md border border-brand-line bg-brand-panel px-3 py-2 text-xs font-semibold text-brand-ink disabled:opacity-60">Cancelar</button>
                </div>
              )}
              <p className="text-xs text-brand-muted">Admite PDF, DOCX, TXT, CSV, JPG y PNG (máx. 10 MB). Las imágenes aún no se leen (sin OCR).</p>
            </div>

            {/* Lista de fuentes */}
            {(form.knowledge_sources || []).length === 0 ? (
              <div className="mt-4 rounded-md border border-dashed border-brand-line px-4 py-8 text-center" data-testid="knowledge-empty">
                <FileText size={22} className="mx-auto mb-2 text-brand-muted/60" />
                <p className="text-sm font-semibold text-brand-ink">Aún no hay fuentes</p>
                <p className="mt-1 text-xs text-brand-muted">Adjunta tu primer documento o pega el enlace de tu web para que CaptAcción entienda tu negocio.</p>
              </div>
            ) : (
              <div className="mt-4 space-y-2" data-testid="knowledge-list">
                {(form.knowledge_sources || []).map((s) => {
                  const st = { pendiente: ["Pendiente", "text-brand-muted border-brand-line bg-brand-panel2"], procesando: ["Procesando", "text-brand-accent border-brand-accent/40 bg-brand-accent/[0.10]"], procesada: ["Procesada", "text-brand-pos border-brand-pos/40 bg-brand-pos/[0.12]"], error: ["Error", "text-brand-neg border-brand-neg/40 bg-brand-neg/[0.12]"] }[s.status] || ["Pendiente", "text-brand-muted border-brand-line bg-brand-panel2"];
                  const processing = s.status === "procesando";
                  return (
                    <div key={s.id} data-testid={`knowledge-item-${s.id}`} className="rounded-md border border-brand-line bg-brand-panel p-3">
                      <div className="flex items-start gap-3">
                        <span className="mt-0.5 text-brand-muted">{s.type === "enlace" ? <Link2 size={15} /> : <FileText size={15} />}</span>
                        <div className="min-w-0 flex-1">
                          <p className="truncate text-sm font-semibold text-brand-ink">{s.name}</p>
                          <p className="text-xs text-brand-muted">{(s.kind || "").toUpperCase()} · {new Date(s.date).toLocaleDateString("es-ES")}</p>
                          {s.status === "procesada" && s.summary && <p className="mt-1.5 whitespace-pre-wrap text-xs leading-relaxed text-brand-muted">{s.summary}</p>}
                          {s.status === "error" && s.error && <p className="mt-1.5 flex items-start gap-1 text-xs leading-relaxed text-brand-neg"><AlertTriangle size={12} className="mt-0.5 shrink-0" /> {s.error}</p>}
                        </div>
                        <span className={`inline-flex shrink-0 items-center gap-1 rounded-md border px-2 py-0.5 text-[10px] font-bold uppercase ${st[1]}`} data-testid={`knowledge-status-${s.id}`}>
                          {processing && <Loader2 size={10} className="animate-spin" />}{st[0]}
                        </span>
                      </div>
                      <div className="mt-2 flex items-center gap-3 pl-8">
                        <button type="button" onClick={() => reprocess(s.id)} disabled={processing || srcBusy === s.id} data-testid={`knowledge-reprocess-${s.id}`} className="inline-flex items-center gap-1 text-xs font-semibold text-brand-muted transition-colors duration-150 hover:text-brand-accent disabled:opacity-50"><RefreshCw size={12} /> {s.status === "error" ? "Reintentar" : "Reprocesar"}</button>
                        {confirmDel === s.id ? (
                          <span className="inline-flex items-center gap-2 text-xs">
                            <span className="text-brand-muted">¿Eliminar?</span>
                            <button type="button" onClick={() => doDelete(s.id)} data-testid={`knowledge-delete-confirm-${s.id}`} className="font-bold text-brand-neg">Sí</button>
                            <button type="button" onClick={() => setConfirmDel(null)} className="font-semibold text-brand-muted">No</button>
                          </span>
                        ) : (
                          <button type="button" onClick={() => setConfirmDel(s.id)} data-testid={`knowledge-delete-${s.id}`} className="inline-flex items-center gap-1 text-xs font-semibold text-brand-muted transition-colors duration-150 hover:text-brand-neg"><Trash2 size={12} /> Eliminar</button>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}

            {/* Resumen general del negocio */}
            {(() => {
              const status = form.summary_status || (form.summary_approved ? "aprobado" : ((form.summary_text || "").trim() ? "pendiente_revision" : "sin_generar"));
              const badge = {
                sin_generar: ["Sin generar", "text-brand-muted border-brand-line bg-brand-panel2"],
                pendiente_revision: ["Pendiente de revisión", "text-brand-accent border-brand-accent/40 bg-brand-accent/[0.10]"],
                aprobado: ["Aprobado", "text-brand-pos border-brand-pos/40 bg-brand-pos/[0.12]"],
                necesita_aprobacion: ["Necesita nueva aprobación", "text-brand-neg border-brand-neg/40 bg-brand-neg/[0.12]"],
              }[status];
              const isApproved = status === "aprobado";
              const stale = !!form.summary_stale;
              const editable = !isApproved || summaryEditing;
              const hasSummary = (form.summary_text || "").trim().length > 0;
              return (
                <div className="mt-6 rounded-md border border-brand-line bg-brand-panel2/40 p-4" data-testid="knowledge-summary-box">
                  <div className="mb-2 flex items-center justify-between gap-2">
                    <p className="text-sm font-bold text-brand-ink">Resumen general de tu negocio</p>
                    <span className={`inline-flex items-center gap-1 rounded-md border px-2 py-0.5 text-[10px] font-bold uppercase ${badge[1]}`} data-testid="knowledge-summary-status">
                      {isApproved && <CheckCircle size={11} />}{badge[0]}
                    </span>
                  </div>
                  <p className="mb-2 text-xs text-brand-muted">Combina tu negocio, tus avatares y los resúmenes de las fuentes procesadas. CaptAcción no genera recomendaciones nuevas hasta que lo apruebes.</p>
                  {stale && (
                    <p data-testid="knowledge-summary-stale" className="mb-2 rounded-md border border-brand-neg/40 bg-brand-neg/[0.10] px-3 py-2 text-xs font-semibold text-brand-neg">
                      Has eliminado una fuente. Regenera el resumen para poder aprobarlo: no puede aprobarse información procedente de una fuente eliminada.
                    </p>
                  )}
                  <textarea
                    data-testid="knowledge-summary"
                    className={`${inputCls} ${!editable ? "opacity-90" : ""}`}
                    rows={7}
                    value={form.summary_text || ""}
                    onChange={(e) => set("summary_text", e.target.value)}
                    readOnly={!editable}
                    placeholder="Pulsa «Generar resumen» para crearlo a partir de tus datos y fuentes. Luego revísalo y apruébalo."
                  />
                  <div className="mt-3 flex flex-wrap items-center gap-2">
                    {!hasSummary ? (
                      <button type="button" onClick={generateSummary} disabled={genBusy} data-testid="knowledge-generate" className="inline-flex items-center gap-2 rounded-md bg-brand-accent px-3 py-2 text-xs font-bold text-brand-canvas transition-all hover:brightness-110 disabled:opacity-60">
                        {genBusy ? <Loader2 size={14} className="animate-spin" /> : <Sparkles size={14} />} {genBusy ? "Generando…" : "Generar resumen"}
                      </button>
                    ) : isApproved && !summaryEditing ? (
                      <button type="button" onClick={reopenSummary} disabled={genBusy} data-testid="knowledge-edit" className="inline-flex items-center gap-2 rounded-md bg-brand-accent px-3 py-2 text-xs font-bold text-brand-canvas transition-all hover:brightness-110 disabled:opacity-60"><Pencil size={14} /> Editar resumen</button>
                    ) : (
                      <>
                        <button type="button" onClick={approveSummary} disabled={genBusy || stale} title={stale ? "Regenera el resumen antes de aprobarlo" : undefined} data-testid="knowledge-approve" className="inline-flex items-center gap-2 rounded-md bg-brand-accent px-3 py-2 text-xs font-bold text-brand-canvas transition-all hover:brightness-110 disabled:opacity-60">
                          {genBusy ? <Loader2 size={14} className="animate-spin" /> : <CheckCircle size={14} />} Guardar y aprobar
                        </button>
                        <button type="button" onClick={generateSummary} disabled={genBusy} data-testid="knowledge-regenerate" className="inline-flex items-center gap-2 rounded-md border border-brand-line bg-brand-panel px-3 py-2 text-xs font-semibold text-brand-ink transition-all hover:border-brand-accent hover:text-brand-accent disabled:opacity-60"><RefreshCw size={13} /> Regenerar</button>
                      </>
                    )}
                  </div>
                </div>
              );
            })()}
          </div>

          <div className="ca-card flex items-center gap-3 p-5">
            <button type="submit" disabled={saving} data-testid="brain-save-button" className="inline-flex items-center gap-2 rounded-md bg-brand-accent px-5 py-2.5 text-sm font-bold text-brand-canvas transition-all duration-150 hover:brightness-110 disabled:opacity-60">
              <Save size={15} /> {saving ? "Guardando…" : "Guardar cambios"}
            </button>
            <p className="text-xs text-brand-muted">Guarda los bloques A–E. Cambiar objetivos o perfiles no invalida el resumen aprobado.</p>
          </div>
          </fieldset>
        </form>
      )}
      {form && <div className="max-w-2xl"><VetoedAccounts businessType={form.business_type} /></div>}
    </div>
  );
}
